/** venture/kithooks.ts — 13·14단계가 여는 순간 앞단계·업체DB·자료수집 포털에서 값을 끌어오는 훅 (2026-09-24)
 *  · useTmCompanies: 연동된 업무관리 업체 정보(법인등록번호·주소·업종·설립일·대표 생년월일·담당자)를 p.tmco 에 스냅샷
 *  · useKitResults : 14단계 자료수집 포털(cp_<coId>_<mode>) 제출 결과를 p.k13res 에 반영 — 창 포커스 복귀 때도 재조회
 *  둘 다 값이 달라졌을 때만 onPatch 해서 저장 트래픽을 만들지 않는다. */
import { useEffect, useRef, useState } from 'react';
import { getProjectCompanies, getCollectResults } from './api';
import { getStore } from './stepcommon';
import type { Project, TmCoInfo, K13Result } from './types';

type Patch = (patch: Partial<Project>) => void;

export function useTmCompanies(p: Project, onPatch: Patch): void {
  const ids = ((p.collect && p.collect.cos) || []).map((co) => co.companyId || '').filter(Boolean).sort().join(',');
  const done = useRef<string>('');
  useEffect(() => {
    if (!ids || done.current === ids) return;
    done.current = ids;
    getProjectCompanies(p.id).then((rows) => {
      const next: Record<string, TmCoInfo> = { ...(p.tmco || {}) };
      rows.forEach((r) => { if (r && r.id) next[r.id] = r; });
      if (JSON.stringify(next) !== JSON.stringify(p.tmco || {})) onPatch({ tmco: next });
    }).catch(() => { done.current = ''; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, p.id]);
}

/** s13 txt 의 cp_* 키 = 14단계 포털 ID 목록 */
export function kitPortalIds(p: Project): string[] {
  const txt = getStore(p, 's13').txt || {};
  return Object.keys(txt).filter((k) => k.startsWith('cp_')).map((k) => txt[k]).filter(Boolean);
}

const slim = (rows: { title: string; type: string; status: string; value: string; uploadId?: string | null }[]): K13Result[] =>
  rows.map((r) => ({ title: r.title, type: r.type, status: r.status, value: r.value, uploadId: r.uploadId || null }));

export function useKitResults(p: Project, onPatch: Patch, tick: number): void {
  const key = kitPortalIds(p).join(',');
  const pRef = useRef(p); pRef.current = p;
  useEffect(() => {
    const ids = key ? key.split(',') : [];
    if (!ids.length) return;
    let alive = true;
    Promise.all(ids.map((cpid) => getCollectResults(p.id, cpid).then((r) => ({ cpid, rows: slim(r.rows) })).catch(() => null)))
      .then((got) => {
        if (!alive) return;
        const cur = pRef.current;
        const next: Record<string, K13Result[]> = { ...(cur.k13res || {}) };
        let changed = false;
        got.forEach((g) => { if (g && JSON.stringify(next[g.cpid] || []) !== JSON.stringify(g.rows)) { next[g.cpid] = g.rows; changed = true; } });
        if (changed) onPatch({ k13res: next });
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, p.id, tick]);
}

/** 창 포커스 복귀 카운터 — 포털을 열어둔 사이 고객이 낸 제출물을 다시 읽어오게 */
export function useFocusTick(): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const on = () => setTick((t) => t + 1);
    window.addEventListener('focus', on);
    return () => window.removeEventListener('focus', on);
  }, []);
  return tick;
}
